/* Anlegget på kartet, til å sjekke og sette merdnumrene for Lusedagboka.
 *
 * Det samme bildet som oppsettet og lusekartet -- satellitt, merdene som
 * polygoner der de faktisk ligger, flåten -- fordi «merd 3 er den ved
 * flåten» er noe man ser, ikke noe man leser. Men uten alt det lusekartet
 * ellers bærer (farger etter lusetall, fisk, strøm): her er spørsmålet bare
 * hvilket nummer hver ring har.
 *
 * Numrene er HTML-knapper oppå kartet og ikke tekst i et kartlag, fordi de
 * skal trykkes på i omnummereringen, med en finger på en båt eller med Tab.
 * Kartlagene tegner ringene; knappene er det man rører.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { SiteLayout } from '../api'
import { penFeature, structureFeature } from '../merdgeo'

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

export type MerdTilstand = 'fast' | 'valgt' | 'venter'

/** Kartet kan bare vises når hver merd er plassert og det finnes et kart å
 *  legge dem på. Ellers tegner Lusedagboka skissen. */
export function kanViseKart(layout: SiteLayout): boolean {
  return !!TOKEN && layout.pens.length > 0
    && layout.pens.every((p) => p.lat != null && p.lon != null)
}

export function MerdKartNummer({ layout, merker, onTrykk, etikett, reserve }: {
  layout: SiteLayout
  /** Nummer og tilstand per pen_id. */
  merker: Record<string, { label: string; state: MerdTilstand }>
  /** Bare under omnummerering: en merd som venter på nummer ble trykket. */
  onTrykk?: (penId: string) => void
  etikett: string
  /** Det som vises om kartet ikke lar seg laste -- skissen, så numrene
   *  fortsatt kan sjekkes og settes. */
  reserve: ReactNode
}) {
  const el = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const lib = useRef<any>(null)
  const knapper = useRef(new Map<string, { mk: any; node: HTMLButtonElement }>())
  const [klar, setKlar] = useState(false)
  const [feilet, setFeilet] = useState(false)
  const siste = useRef({ merker, onTrykk })
  siste.current = { merker, onTrykk }

  useEffect(() => {
    if (!TOKEN || map.current) return
    let dead = false
    let m: any
    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dead || !el.current) return
        lib.current = maplibregl
        const p0 = layout.pens[0]!
        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          dragRotate: false,
          pitchWithRotate: false,
          style: {
            version: 8,
            sources: {
              satellite: {
                type: 'raster',
                tiles: [`https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${TOKEN}`],
                tileSize: 256,
                attribution: '© Mapbox · © OpenStreetMap',
              },
            },
            layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
          },
          center: [p0.lon as number, p0.lat as number],
          zoom: 15,
        })
        map.current = m
        m.touchZoomRotate.disableRotation()
        m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
        m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
        m.on('load', () => {
          m.addSource('ringer', { type: 'geojson',
                                  data: { type: 'FeatureCollection', features: [] } })
          // Fylt der nummeret er satt, svakt ellers: det som gjenstår skal
          // synes uten å telle.
          m.addLayer({
            id: 'ringer-fyll', type: 'fill', source: 'ringer',
            paint: {
              'fill-color': ['case', ['==', ['get', 'kind'], 'structure'], '#93ab9f', '#b4d9c7'],
              'fill-opacity': ['match', ['get', 'state'],
                'valgt', 0.75, 'venter', 0.08, 'structure', 0.5, 0.28],
            },
          })
          m.addLayer({
            id: 'ringer-kant', type: 'line', source: 'ringer',
            paint: {
              'line-color': ['match', ['get', 'state'], 'venter', '#e6f1ea', '#b4d9c7'],
              'line-width': ['match', ['get', 'state'], 'venter', 1.5, 2.5],
            },
          })
          setKlar(true)
        })
        m.on('error', (e: any) => {
          if (e?.error?.status === 401 || e?.error?.status === 403) setFeilet(true)
        })
      } catch {
        if (!dead) setFeilet(true)
      }
    })()
    return () => {
      dead = true
      knapper.current.clear()
      try { m?.remove() } catch { /* allerede borte */ }
    }
    // Kartet bygges én gang; endringer tegnes i effekten under.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ramme inn anlegget én gang, og la kameraet være i fred etterpå.
  const rammet = useRef(false)
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!klar || !m || !maplibregl) return

    const features = [
      ...layout.pens.map((p) => penFeature(p, {
        kind: 'pen', state: merker[p.pen_id]?.state ?? 'fast' })),
      ...layout.structures.map((s) => structureFeature(s, {
        kind: 'structure', state: 'structure' })),
    ].filter(Boolean)
    m.getSource('ringer')?.setData({ type: 'FeatureCollection', features })

    for (const p of layout.pens) {
      const merke = merker[p.pen_id] ?? { label: p.pen_no, state: 'fast' as const }
      let k = knapper.current.get(p.pen_id)
      if (!k) {
        const node = document.createElement('button')
        node.type = 'button'
        node.classList.add('ldb-kartmerd')
        node.addEventListener('click', (e) => {
          e.stopPropagation()
          const s = siste.current
          if (s.onTrykk && s.merker[p.pen_id]?.state === 'venter') s.onTrykk(p.pen_id)
        })
        const mk = new maplibregl.Marker({ element: node })
          .setLngLat([p.lon as number, p.lat as number]).addTo(m)
        k = { mk, node }
        knapper.current.set(p.pen_id, k)
      }
      const trykkbar = !!onTrykk && merke.state === 'venter'
      // classList, never className: maplibre puts its own positioning class on
      // the element, and overwriting it stacks every number in the corner.
      k.node.classList.remove('fast', 'valgt', 'venter')
      k.node.classList.add(merke.state)
      k.node.textContent = merke.label
      k.node.disabled = !trykkbar
      k.node.tabIndex = trykkbar ? 0 : -1
      k.node.setAttribute('aria-label',
        merke.state === 'venter' ? 'Merd uten nummer' : `Merd ${merke.label}`)
    }

    if (!rammet.current) {
      rammet.current = true
      const pts = [
        ...layout.pens.map((p) => [p.lon as number, p.lat as number]),
        ...layout.structures.map((s) => [s.lon, s.lat]),
      ] as [number, number][]
      const b = pts.reduce((acc, pt) => acc.extend(pt),
                           new maplibregl.LngLatBounds(pts[0], pts[0]))
      m.fitBounds(b, { padding: 46, maxZoom: 17.5, duration: 0 })
    }
  }, [klar, layout, merker, onTrykk])

  if (feilet) return <>{reserve}</>
  return <div ref={el} className="ldb-kart" role="group" aria-label={etikett} />
}
